/**
 * Matrix family: heatmap.
 *
 * Magnitude is a sequential job, so cells take the single-hue sequential ramp,
 * never the categorical slots — those encode identity, which a heatmap cell
 * does not have.
 */
import type { EChartsOption } from 'echarts';
import { chartTokens, type ChartMedia, type ChartMode } from '@/tokens/chart';
import type { ChartConfig, ChartRow } from '../types';
import { baseOption } from './shared';

const toNumber = (value: unknown): number => {
  const n = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(n) ? n : 0;
};

const uniqueInOrder = (values: readonly string[]): string[] => [...new Set(values)];

/** Room below the plot for the horizontal visualMap scale. */
const VISUAL_MAP_INSET = 48;

export function buildMatrixOption(
  config: ChartConfig,
  data: readonly ChartRow[],
  mode: ChartMode,
  media: ChartMedia,
): EChartsOption {
  const tokens = chartTokens[mode];
  const entry = config.series.find((s) => !s.hidden);
  const xField = entry?.fields.x;
  const yField = entry?.fields.y;
  const valueField = entry?.fields.value;

  const usable =
    entry &&
    xField &&
    yField &&
    valueField &&
    data.length > 0 &&
    xField in data[0] &&
    yField in data[0] &&
    valueField in data[0];

  if (!usable) {
    if (entry && data.length > 0) {
      console.warn(
        `[octopus-ui/chart] heatmap series "${entry.name}" references fields not present in ` +
          'the data; nothing to render.',
      );
    }
    return { ...baseOption(config, mode, media), series: [] } as EChartsOption;
  }

  const xs = uniqueInOrder(data.map((row) => String(row[xField])));
  const ys = uniqueInOrder(data.map((row) => String(row[yField])));
  const cells = data.map((row) => [
    xs.indexOf(String(row[xField])),
    ys.indexOf(String(row[yField])),
    toNumber(row[valueField]),
  ]);
  const values = cells.map((cell) => cell[2]);

  const axis = (categories: string[]) => ({
    type: 'category' as const,
    data: categories,
    axisLine: { lineStyle: { color: tokens.ink.axis } },
    axisTick: { show: false },
    axisLabel: { color: tokens.ink.label },
    // The cells themselves carry the visual; alternating split bands behind
    // them would compete with the sequential ramp.
    splitArea: { show: false },
  });

  const base = baseOption(config, mode, media);

  return {
    ...base,
    // The visualMap sits along the bottom edge and, like the legend, is invisible
    // to `containLabel` — without extra room it lands on the category labels.
    grid: { ...(base.grid as object), bottom: VISUAL_MAP_INSET },
    aria: {
      ...(base.aria as object),
      // Decals are secondary encoding for categorical *identity*. A heatmap
      // encodes magnitude through lightness, so hatching every cell competes
      // with the only channel that carries meaning here.
      decal: { show: false },
    },
    xAxis: axis(xs),
    yAxis: axis(ys),
    visualMap: {
      min: Math.min(...values),
      max: Math.max(...values),
      calculable: media === 'screen',
      orient: 'horizontal',
      left: 'center',
      bottom: 0,
      textStyle: { color: tokens.ink.label },
      inRange: { color: [...tokens.sequential] },
    },
    series: [
      {
        id: entry.id,
        name: entry.name,
        type: 'heatmap',
        data: cells,
        itemStyle: { borderColor: tokens.surface, borderWidth: 2 },
      },
    ],
  } as EChartsOption;
}
